import { ClientTags, QueryParamKeys, QueryParamParsers } from '@evenfinancial/even-ts-static'
import { ensureArray } from 'embed/utils/arrays/ensureArray'
import { AssertIsInBrowser } from 'embed/utils/errors/NotInBrowserError'
import { ParsedParamGetters, ParsedParams, QUERY_PARAM_BLACKLIST, RawQueryString, TAG_PREFIX } from './types'

/**
 * Parses standard params into a record with their expected types
 * @param {Record<string, string>} params
 * @return {ParsedParams}
 */
const parseParams = (params: Record<string, string>): ParsedParams => {
  const parsedParams: ParsedParams = {}

  Object.entries(params).forEach(([key, value]) => {
    if (QueryParamParsers[key]) {
      const parser = QueryParamParsers[key]
      parsedParams[key] = parser(value)
    } else {
      parsedParams[key] = value
    }
  })
  return parsedParams
}

/**
 * Removes scary values from untrusted sources
 * @param {Record<string, string>} params
 * @return {Record<string, string>}
 */
const sanitizeParams = (params: Record<string, string>): Record<string, string> => {
  const sanitizedParams = Object.entries(params).filter((param) => !QUERY_PARAM_BLACKLIST.includes(param[0]))
  return Object.fromEntries(sanitizedParams)
}

/**
 * Formats client tags which contain a '.' in key names
 * @param {ParsedParams} parsedParams
 * @return {ClientTags}
 */
const parseClientTags = (parsedParams: ParsedParams): ClientTags =>
  Object.keys(parsedParams).reduce((acc, key) => {
    if (key.startsWith(TAG_PREFIX)) {
      const value = parsedParams[key]
      const plainKey = key.replace(TAG_PREFIX, '')
      if (value) {
        acc[plainKey] = ensureArray(value)
      }
    }
    return acc
  }, {})

/**
 * Formats raw query string and returns getters for search param operations
 * @param {RawQueryString} queryString
 * @return {ParsedParamGetters}
 */
export const parseRawParamString = (queryString: RawQueryString): ParsedParamGetters => {
  const searchParams = Object.fromEntries(new URLSearchParams(queryString).entries())
  const sanitizedParams = sanitizeParams(searchParams)
  const parsedParams = parseParams(sanitizedParams)
  const parsedClientTags = parseClientTags(parsedParams)

  return {
    get(key: QueryParamKeys) {
      return parsedParams[key]
    },
    getAll() {
      return parsedParams
    },
    getClientTags() {
      return parsedClientTags
    },
    getRawURLSearchParams() {
      return searchParams
    },
  }
}

let queryParamsInstance: ParsedParamGetters

/**
 * Initializes the shared query params instance using the passed query string
 * * @param queryString
 */
export const initQueryParams = (queryString: string) => {
  queryParamsInstance = parseRawParamString(queryString)
}
/**
 * Initializes the shared query params instance using the window's values
 * @throws if not in browser
 */
export const initQueryParamsFromWindow = () => {
  AssertIsInBrowser()
  const windowParams = window?.location?.search
  queryParamsInstance = parseRawParamString(windowParams)
}

/**
 * Gets the shared query params instance, attempting to initialize it from window if undefined
 * @throws if not in browser and {initQueryParams} has not been called
 */
export const getQueryParams = () => {
  if (!queryParamsInstance) {
    initQueryParamsFromWindow()
  }
  return queryParamsInstance
}
